import React from 'react';
import Header from './common/Header';
import Footer from './common/Footer';

const RentalsAndPurchasesHelpPage = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent">
            How Rentals and Purchases Work
          </h1>
          <p className="text-gray-400">
            Everything you need to know about renting and buying on ChillFree TV
          </p>
        </div>

        {/* Introduction */}
        <section className="mb-12">
          <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
            <p className="text-gray-300 leading-relaxed mb-4">
              ChillFree TV offers two ways to watch select premium titles:
            </p>
            <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
              <li>Rent for a limited time</li>
              <li>Buy for ongoing access</li>
            </ul>
            <p className="text-gray-300 leading-relaxed">
              Most of our library is still free with ads. Rentals and purchases apply only to select premium titles. Here is exactly how it works.
            </p>
          </div>
        </section>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          
          {/* 1. Renting a Movie */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-teal-400">1. Renting a Movie</h2>
            <div className="space-y-6">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300 leading-relaxed mb-4">
                  When you rent a movie, you are paying for temporary streaming access.
                </p>
                <h3 className="text-lg font-semibold mb-4 text-green-400">What happens when you rent</h3>
                <ul className="text-gray-300 list-disc list-inside space-y-1">
                  <li>You pay once</li>
                  <li>Your rental becomes available in your account</li>
                  <li>The rental window starts when you press play</li>
                  <li>You can watch as many times as you want during the rental period</li>
                </ul>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-green-400">How long do rentals last?</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Rental windows vary by title. The exact rental period is shown before you confirm your purchase.
                </p>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Once the rental period expires, access automatically ends.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  If you do not start playback, the rental window may begin automatically based on platform rules.
                </p>
              </div>
            </div>
          </section>

          {/* 2. Buying a Movie */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-teal-400">2. Buying a Movie (Digital Purchase)</h2>
            <div className="space-y-6">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300 leading-relaxed mb-4">
                  When you buy a movie, this is called Electronic Sell Through (EST).
                </p>
                <h3 className="text-lg font-semibold mb-4 text-green-400">What you receive</h3>
                <ul className="text-gray-300 list-disc list-inside space-y-1">
                  <li>Ongoing digital streaming access</li>
                  <li>Access tied to the platform account you used to purchase</li>
                  <li>Ability to rewatch anytime while the title remains available</li>
                </ul>
              </div>
              
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-yellow-400">Important to know</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Buying a movie does not mean you own the copyright or receive a downloadable file unless specifically stated. It means you have a personal streaming license through the platform you purchased on.
                </p>
                <p className="text-gray-300 leading-relaxed mb-2">Access may depend on:</p>
                <ul className="text-gray-300 list-disc list-inside space-y-1">
                  <li>Platform availability</li>
                  <li>Device compatibility</li>
                  <li>Regional licensing rights</li>
                  <li>Ongoing distribution agreements</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. Where Your Payment Is Processed */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-teal-400">3. Where Your Payment Is Processed</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                ChillFree TV does not directly process your credit card.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Payments are handled by the app store or TV platform you used, such as:
              </p>
              <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                <li>Apple App Store</li>
                <li>Google Play Store</li>
                <li>Roku</li>
                <li>Amazon Fire TV</li>
                <li>Samsung</li>
                <li>LG</li>
                <li>Vizio</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mb-4">
                Your charge will appear under that platform's billing name.
              </p>
              <p className="text-gray-300 leading-relaxed">
                If you need a refund, it must be requested directly through the platform where the purchase was made.
              </p>
            </div>
          </section>

          {/* 4. Refunds and Billing Questions */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-teal-400">4. Refunds and Billing Questions</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                Refund policies are controlled by the platform used for purchase.
              </p>
              <p className="text-gray-300 leading-relaxed mb-2">For example:</p>
              <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                <li>Apple purchases must be refunded through Apple</li>
                <li>Google Play purchases must be refunded through Google</li>
                <li>Roku purchases must be refunded through Roku</li>
              </ul>
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-gray-300 leading-relaxed">
                  ChillFree TV cannot directly issue refunds for purchases made through third-party platforms.
                </p>
              </div>
              <p className="text-gray-300 leading-relaxed mb-2">If you are unsure where your charge came from, check:</p>
              <ul className="text-gray-300 list-disc list-inside space-y-1">
                <li>The device you used</li>
                <li>The email receipt from the platform</li>
                <li>Your app store purchase history</li>
              </ul>
            </div>
          </section>

          {/* 5. Restoring a Purchase */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-teal-400">5. Restoring a Purchase</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                If you bought a movie and cannot see it:
              </p>
              <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                <li>Make sure you are logged into the same account used for purchase</li>
                <li>Use the "Restore Purchases" option if available</li>
                <li>Check that the title is still available in your region</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                If you still need help, contact us.
              </p>
            </div>
          </section>

          {/* 6. Playback Issues */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-teal-400">6. Playback Issues</h2>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <p className="text-gray-300 leading-relaxed mb-4">
                If your rental or purchase is not playing:
              </p>
              <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                <li>Check your internet connection</li>
                <li>Restart the app</li>
                <li>Restart your device</li>
                <li>Make sure your app is updated</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                If the issue continues, contact support.
              </p>
            </div>
          </section>

          {/* 7. Need Help? */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-teal-400">7. Need Help?</h2>
            <div className="bg-teal-900/20 border border-teal-500/30 rounded-lg p-6">
              <p className="text-gray-300 leading-relaxed mb-4">
                We are here to help.
              </p>
              <p className="text-gray-300 leading-relaxed mb-2">For questions about:</p>
              <ul className="text-gray-300 list-disc list-inside space-y-1 mb-4">
                <li>Rentals</li>
                <li>Purchases</li>
                <li>Playback</li>
                <li>Account access</li>
                <li>Transaction validation</li>
              </ul>
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-gray-300 mb-1"><strong>Email:</strong></p>
                  <a href="mailto:support@chillfree.tv" className="text-teal-400 hover:text-teal-300 underline">
                    support@chillfree.tv
                  </a>
                </div>
                <div>
                  <p className="text-gray-300 mb-1"><strong>For privacy-related requests:</strong></p>
                  <a href="mailto:datarequest@chillfree.tv" className="text-teal-400 hover:text-teal-300 underline">
                    datarequest@chillfree.tv
                  </a>
                </div>
              </div>
              <div className="border-t border-teal-500/30 pt-4">
                <p className="text-gray-300 leading-relaxed mb-2">Please include:</p>
                <ul className="text-gray-300 list-disc list-inside space-y-1">
                  <li>The title name</li>
                  <li>The device used</li>
                  <li>The platform where purchased</li>
                  <li>The date of purchase</li>
                </ul>
                <p className="text-gray-400 text-sm mt-4">
                  This helps us resolve your issue faster.
                </p>
              </div>
            </div>
          </section>

          {/* Final Reminder */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-teal-400">Final Reminder</h2>
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
              <p className="text-gray-300 leading-relaxed mb-4">
                Most content on ChillFree TV is free with ads.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Rentals and purchases apply only to select premium titles.
              </p>
              <p className="text-gray-300 leading-relaxed mb-2">Always confirm:</p>
              <ul className="text-gray-300 list-disc list-inside space-y-1">
                <li>Rental length</li>
                <li>Price</li>
                <li>Platform account</li>
                <li>Device compatibility</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                before completing your transaction.
              </p>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RentalsAndPurchasesHelpPage;
